import { AxiosError } from 'axios';

import { getEmptyOffsetPagedResults, OffsetPagedResults } from '~interfaces';
import { SkcDataResponse, SkcOffsetPagedResponse } from '~interfaces/responses';
import i18n from '~lib/i18n';
import ApiError from '~services/apiError';
import SkopeiConnectService from '~services/skopeiConnectService';

import Organisation from '../../../interfaces/organisation';

class OrganisationsService extends SkopeiConnectService {
	protected readonly path = 'clients';

	/**
	 * Get a paginated list of organisations
	 *
	 * @param page The page number, starting at 1
	 * @param pageSize The amount of results per page
	 * @param searchQuery The search query to filter organisations.
	 *
	 * @returns A list of organisations if successful
	 */
	async getOrganisations(params: {
		page: string;
		pageSize: string;
		searchQuery?: string;
		signal?: AbortSignal;
	}): Promise<OffsetPagedResults<Organisation>> {
		const endpoint = this.createEndpoint('v2', ['details'], true);
		return await this.client
			.get<SkcOffsetPagedResponse<OrganisationResponse>>(endpoint, {
				signal: params.signal,
				params: {
					'page-number': params.page,
					'page-size': params.pageSize,
					search: params.searchQuery?.trim() || undefined,
				},
			})
			.then((response) => {
				if (!response.data) {
					return Promise.resolve(
						getEmptyOffsetPagedResults<Organisation>(Number(params.page), Number(params.pageSize))
					);
				}
				return Promise.resolve(
					this.mapSkcOffsetPagedResponse(response.data, OrganisationsService.fromResponse)
				);
			});
	}

	public getOrganisationsCacheKey(searchParams: URLSearchParams) {
		return [this.path, searchParams.toString()] as const;
	}

	// public getOrganisationsCacheKey(pageNumber: string, pageSize: string, searchQuery?: string) {
	// 	return [this.path, pageNumber, pageSize, searchQuery] as const;
	// }

	/**
	 * Get an organisation by id.
	 *
	 * @param organisationId The id of the organisation.
	 *
	 * @returns The organisation.
	 */
	async getOrganisationById(organisationId: string): Promise<Organisation> {
		const endpoint = this.createEndpoint('v2', [organisationId], true);
		return await this.client
			.get<OrganisationResponse>(endpoint)
			.then((response) => {
				if (!response || response.data == undefined) {
					const deviceNotFoundError = new ApiError(new AxiosError(''));
					deviceNotFoundError.setDescription(i18n.t('organisationNotFound', { ns: 'errors' }));
					return Promise.reject(deviceNotFoundError);
				}

				return Promise.resolve(OrganisationsService.fromResponse(response.data));
			})
			.catch((error: ApiError) => {
				if (error.errorCode == 'SKC_004') {
					error.setDescription(i18n.t('organisationNotFound', { ns: 'errors' }));
				}
				return Promise.reject(error);
			});
	}

	public getOrganisationByIdCacheKey(organisationId?: string) {
		return [this.path, organisationId] as const;
	}

	/**
	 * Add an organisation.
	 *
	 * @param organisation The organisation to add.
	 *
	 * @returns The added organisation.
	 */
	async addOrganisation(organisation: Partial<Organisation>): Promise<Organisation> {
		const endpoint = this.createEndpoint('v2', [], true);
		return await this.client.post<OrganisationResponse>(endpoint, organisation).then((response) => {
			if (!response || response.data == undefined) {
				const deviceNotFoundError = new ApiError(new AxiosError(''));
				deviceNotFoundError.setDescription(i18n.t('organisationNotFound', { ns: 'errors' }));
				return Promise.reject(deviceNotFoundError);
			}
			return Promise.resolve(OrganisationsService.fromResponse(response.data));
		});
	}

	/**
	 * Update an organisation
	 *
	 * @param organisationId The id of the organisation
	 * @param organisation The organisation to update
	 *
	 * @returns The updated organisation
	 */
	async updateOrganisation(
		organisationId: string,
		organisation: Partial<Organisation>
	): Promise<Organisation> {
		const endpoint = this.createEndpoint('v2', [organisationId], true);
		return await this.client
			.put<OrganisationResponse | SkcDataResponse<OrganisationResponse>>(endpoint, organisation)
			.then((response) => {
				const payload =
					typeof response.data === 'object' && response.data !== null && 'data' in response.data ?
						response.data.data
					:	response.data;

				if (!payload) {
					const deviceNotFoundError = new ApiError(new AxiosError(''));
					deviceNotFoundError.setDescription(i18n.t('organisationNotFound', { ns: 'errors' }));
					return Promise.reject(deviceNotFoundError);
				}
				return Promise.resolve(OrganisationsService.fromResponse(payload));
			});
	}

	/**
	 * Delete an organisation.
	 *
	 * @param id The identifier of the organisation.
	 *
	 * @returns Empty response.
	 */
	async deleteOrganisation(id: string): Promise<null> {
		const endpoint = this.createEndpoint('v2', [id], true);
		await this.client.delete(endpoint);

		return null;
	}

	/**
	 * Assigns devices to an organisation.
	 *
	 * @param deviceIds The ids of the devices to assign.
	 * @param organisationId The id of the organisation to assign the devices to.
	 * @param signal The signal for aborting.
	 */
	async assignDevicesToOrganisation(
		deviceIds: string[],
		organisationId: string,
		signal?: AbortSignal
	): Promise<void> {
		const endpoint = this.createEndpoint('v2', [organisationId, 'assign-devices'], true);
		await this.client.post<string>(
			endpoint,
			deviceIds.flatMap((value) => Number(value)),
			{
				signal: signal,
				headers: {
					OverwrittenClientId: organisationId,
				},
			}
		);
	}

	/**
	 * Batch unassign devices from an organisation.
	 *
	 * @param deviceIds The ids to unassign.
	 */
	async unassignDevices(deviceIds: string[]): Promise<null> {
		const endpoint = this.createEndpoint('v2', ['unassign-devices'], true);
		await this.client.post(endpoint, deviceIds);
		return null;
	}

	/**
	 * Assigns keys to an organisation.
	 *
	 * @param deviceIds The ids of the keys to assign.
	 * @param organisationId The id of the organisation to assign the keys to.
	 * @param signal The signal for aborting.
	 */
	async assignKeysToOrganisation(
		keyIds: string[],
		organisationId: string,
		signal?: AbortSignal
	): Promise<void> {
		const endpoint = this.createEndpoint('v2', [organisationId, 'assign-keys'], true);
		await this.client.post<string>(endpoint, keyIds, {
			signal: signal,
			headers: {
				OverwrittenClientId: organisationId,
			},
		});
	}

	/**
	 * Batch unassign keys from an organisation.
	 *
	 * @param deviceIds The ids to unassign.
	 */
	async unassignKeys(keyIds: string[]): Promise<null> {
		const endpoint = this.createEndpoint('v2', ['unassign-keys'], true);
		await this.client.post(endpoint, keyIds);
		return null;
	}

	/**
	 * Model parsing
	 */
	static fromResponse(data: OrganisationResponse): Organisation {
		const { id, ...rest } = data;

		return {
			...rest,
			id: data.id.toString(),
		};
	}
}

interface OrganisationResponse {
	id: string;
	name: string;
	emailAddress?: string;
	phoneNumber?: string;
	devicesCount?: number;
	keysCount?: number;
	usersCount?: number;
	creationDate: Date;
}

export default OrganisationsService;
